import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { resolveImageUrl } from '../lib/imageUrl';
import { Calendar, Clock, Users, ArrowRight, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrainingPage() {
  const { t, getLocalizedContent } = useLanguage();
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTrainings();
  }, []);

  const fetchTrainings = async () => {
    try {
      const response = await axios.get(`${API}/trainings`);
      setTrainings(response.data);
    } catch (error) {
      console.error('Error fetching trainings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!selectedTraining) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/trainings/${selectedTraining.id}/register`, formData);
      toast.success('Inscription envoyée avec succès !');
      setShowRegisterModal(false);
      setFormData({ name: '', email: '', phone: '', message: '' });
      setSelectedTraining(null);
    } catch (error) {
      toast.error('Erreur lors de l\'inscription');
    } finally {
      setSubmitting(false);
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div data-testid="training-page">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <span className="inline-block px-4 py-1 bg-[#0B6AB3]/20 text-[#0B6AB3] text-sm font-medium rounded-full mb-4">
              Développez vos compétences
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-white font-['Montserrat']">
              {t('training_title')}
            </h1>
            <p className="text-gray-300 mt-4 max-w-2xl mx-auto text-lg">
              {t('training_subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Trainings Grid */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B6AB3]"></div>
            </div>
          ) : trainings.length === 0 ? (
            <div className="text-center py-20 text-gray-500">
              Aucune formation disponible pour le moment
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {trainings.map((training, index) => (
                <motion.div
                  key={training.id}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full border-0 shadow-sm hover:shadow-xl transition-all duration-300" data-testid={`training-card-${index}`}>
                    <div className="relative h-48 overflow-hidden rounded-t-lg">
                      <img
                        src={resolveImageUrl(training.image_url) || 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800'}
                        alt={getLocalizedContent(training.title)}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <CardContent className="p-6">
                      <h3 className="font-semibold text-xl text-[#0F172A] mb-3 font-['Montserrat']">
                        {getLocalizedContent(training.title)}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {getLocalizedContent(training.description)}
                      </p>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <Clock className="h-4 w-4 mr-2 text-[#0B6AB3]" />
                          {t('training_duration')}: {training.duration}
                        </div>
                        {training.start_date && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="h-4 w-4 mr-2 text-[#0B6AB3]" />
                            {t('training_start')}: {new Date(training.start_date).toLocaleDateString('fr-FR')}
                          </div>
                        )}
                        <div className="flex items-center text-sm text-gray-600">
                          <Users className="h-4 w-4 mr-2 text-[#0B6AB3]" />
                          Max: {training.max_participants} participants
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t">
                        <div className="text-xl font-bold text-[#0B6AB3]">
                          {training.price?.toLocaleString()} {t('common_fcfa')}
                        </div>
                        <Button
                          onClick={() => {
                            setSelectedTraining(training);
                            setShowRegisterModal(true);
                          }}
                          className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white"
                          data-testid={`register-btn-${index}`}
                        >
                          {t('training_register')}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Registration Modal */}
      <Dialog open={showRegisterModal} onOpenChange={setShowRegisterModal}>
        <DialogContent className="max-w-md" data-testid="register-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0F172A]">
              {t('training_register')}
            </DialogTitle>
          </DialogHeader>
          {selectedTraining && (
            <div className="mt-2 mb-4 p-3 bg-[#F8F9FA] rounded-lg">
              <p className="font-medium text-[#0F172A]">{getLocalizedContent(selectedTraining.title)}</p>
              <p className="text-sm text-gray-600">{selectedTraining.price?.toLocaleString()} FCFA</p>
            </div>
          )}
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <Label htmlFor="name">{t('contact_name')} *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="register-name"
              />
            </div>
            <div>
              <Label htmlFor="email">{t('contact_email')} *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="register-email"
              />
            </div>
            <div>
              <Label htmlFor="phone">{t('contact_phone')} *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                data-testid="register-phone"
              />
            </div>
            <div>
              <Label htmlFor="message">{t('contact_message')}</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={3}
                data-testid="register-message"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#0B6AB3] hover:bg-[#095c9e] text-white"
              disabled={submitting}
              data-testid="register-submit"
            >
              {submitting ? 'Envoi en cours...' : t('training_register')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
